import React, { useContext, useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  styled,
  Drawer,
  IconButton,
  useMediaQuery,
  TextField,
  Box,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import ListIcon from '@mui/icons-material/List';
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import logo from "../../Assets/images/bigfive_logo.png";
import CancelIcon from '@mui/icons-material/Cancel';
import {
  primaryColor,
  PageBackgroundColor,
  fontFamilyall,
} from "../UtilColorPalette/UtilColorPalette";
import { KnowledgeBaseContext } from "../../Components/knowledgeBaseContext/KnowledgeBaseContext";

/* -------------------- Debounce Hook (in same file) -------------------- */
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

/* -------------------- Styled Components -------------------- */
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: "#fff",
  boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
  padding: theme.spacing(0, 2),
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  justifyContent: "space-between",
  padding: theme.spacing(1, 0),
}));

const NavContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const MobileNavContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
  backgroundColor: "#fff",
  height: "100%",
}));

const NavText = styled(Typography)(({ theme, active }) => ({
  color: active ? primaryColor : "#757575",
  fontWeight: active ? 600 : 400,
  textTransform: "uppercase",
  fontSize: "16px",
  fontFamily: fontFamilyall,
  marginLeft: theme.spacing(3),
  marginRight: theme.spacing(3),
  textDecoration: "none",
  cursor: "pointer",
  "&:hover": {
    color: primaryColor,
  },
}));

const MobileNavText = styled(Typography)(({ theme, active }) => ({
  color: active ? primaryColor : "#757575",
  fontWeight: active ? 600 : 400,
  textTransform: "uppercase",
  fontSize: "16px",
  fontFamily: fontFamilyall,
  textDecoration: "none",
  padding: theme.spacing(2, 1),
  cursor: "pointer",
  "&:hover": {
    color: primaryColor,
  },
}));

const Logo = styled("img")(({ theme }) => ({
  width: 80,
  height: 80,
  [theme.breakpoints.down("sm")]: {
    width: 70,
    height: 70,
  },
}));

const linkStyle = {
  textDecoration: "none",
  color: "white",
  "&:hover": {
    color: "white",
    textDecoration: "none",
  },
};

/* -------------------- Main Component -------------------- */
const NavbarWithSearch = () => {
  const [activeItem, setActiveItem] = useState("Home");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { searchTerm, setSearchTerm, searchString, setSearchString, categories, individualArticle, selectedCategoryName, knowledgebase, mobileOpenSideBar,
    setMobileOpensideBar } =
    useContext(KnowledgeBaseContext);
  console.log(individualArticle, "article iddd")
  const [inputValue, setInputValue] = useState(searchString || "");
  const debouncedSearch = useDebounce(inputValue, 600);
  const [loading, setLoading] = useState(false);

  const isMobile = useMediaQuery("(max-width: 900px)");
  const isSmall = useMediaQuery("(max-width: 600px)");
  const location = useLocation();

  const navigate = useNavigate();

  /* 🔹 Non-document path: debounce search handling */
  useEffect(() => {
    if (!location.pathname.includes("/document")) {
      if (debouncedSearch.trim() !== "") {
        setLoading(true);
        setSearchString(debouncedSearch);

        // simulate async (API call or filter)
        setTimeout(() => setLoading(false), 500);
      } else {
        setSearchString("");
        knowledgebase();
      }
    }
  }, [debouncedSearch, location.pathname, setSearchString]);

  const navItems = [
    { name: "Home", url: "https://bigfivecrm.com" },
    { name: "Features", url: "https://bigfivecrm.com/features/" },
    { name: "Pricing", url: "https://bigfivecrm.com/pricing/#pricing" },
    { name: "Products", url: "https://bigfivecrm.com/products/" },
    { name: "About", url: "https://bigfivecrm.com/about-us/" },
    { name: "Contact", url: "https://bigfivecrm.com/contact/" },
  ];

  const handleNavClick = (item) => {
    setActiveItem(item);
    setMobileOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleClearSearch = () => {
    if (location.pathname.includes("/document")) {
      setSearchTerm("");
    } else {
      setInputValue("");
      setSearchString("");
      knowledgebase();
    }
  };

  const mobileMenu = (
    <MobileNavContainer>
      {navItems.map((item) => (
        <MobileNavText
          key={item.name}
          active={activeItem === item.name}
          onClick={() => handleNavClick(item.name)}
          component="a"
          href={item.url}
        >
          {item.name}
        </MobileNavText>
      ))}
    </MobileNavContainer>
  );
  // const category = categories.find(
  //         (item) => item.recordid === location.pathname.split("/")[2] && item.isactive
  //       );

  //       console.log(category,categories,location,"jjmk")
  return (
    <>
      <StyledAppBar position="fixed">
        <StyledToolbar>
          <Logo src={logo} alt="BigFive Logo" />
          {!isMobile && (
            <NavContainer>
              {navItems.map((item) => (
                <NavText
                  key={item.name}
                  active={activeItem === item.name}
                  onClick={() => handleNavClick(item.name)}
                  component="a"
                  href={item.url}
                >
                  {item.name}
                </NavText>
              ))}
            </NavContainer>
          )}
          {isMobile && (
            <IconButton
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ color: "#757575" }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </StyledToolbar>
        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          sx={{ "& .MuiDrawer-paper": { width: "75%", maxWidth: 320 } }}
        >
          {mobileMenu}
        </Drawer>
      </StyledAppBar>

      {/* Search bar / Article Go Back */}
      {location.pathname.includes("/article") ? (
        <Box
          sx={{
            position: "fixed",
            top: isMobile ? "85px" : "90px",
            left: 0,
            right: 0,
            backgroundColor: primaryColor,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            py: 1,
            zIndex: 1100,
            px: 2,
            gap: 1,
          }}
        >
          {/* Back button */}
          <IconButton
            aria-label="go back"
            onClick={() =>{ 
              navigate("/knowledgebase/")
              setSearchString(decodeURIComponent(location.pathname.split("/")[3]))
              setInputValue(decodeURIComponent(location.pathname.split("/")[3]))
            }}
            sx={{ color: "white" }}
          >
            <ArrowBackIcon />
          </IconButton>

          {/* Category name */}
          <Typography
            variant="h6"
            sx={{
              color: "white",
              fontFamily: fontFamilyall,
              fontSize: "16px",
              fontWeight: 700,
            }}
          >
            {"Back to Search"}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            position: "fixed",
            top: isMobile ? "85px" : "90px",
            left: 0,
            right: 0,
            backgroundColor: primaryColor,
            display: "flex",
            justifyContent:
              location.pathname.includes("/document") ? "flex-start" : "center",
            alignItems: "center",
            py: 1,
            zIndex: 1100,
            px: 2,
            gap: 1,
          }}
        >
          {location.pathname.includes("/document") && (
            <>
              {
                isMobile &&
                <IconButton
                  aria-label="open drawer"
                  edge="end"
                  onClick={() => setMobileOpensideBar(true)}
                  sx={{ ...linkStyle }}
                >
                  <ListIcon />
                </IconButton>
              }

              <IconButton aria-label="home" component="a" href={`/knowledgebase/`} sx={{ ...linkStyle }}>
                <HomeIcon />
              </IconButton>
            </>
          )}

          {
            (!isMobile && location.pathname.includes("/document") || (location.pathname == "/knowledgebase/" || location.pathname == "/knowledgebase" )) &&
            <TextField
              placeholder="Search here..."
              variant="outlined"
              value={location.pathname.includes("/document") ? searchTerm : inputValue}
              onChange={(e) =>
                location.pathname.includes("/document")
                  ? setSearchTerm(e.target.value)
                  : setInputValue(e.target.value)
              }
              InputProps={{
                // startAdornment: (
                //   <InputAdornment position="start">
                //     <SearchIcon color="action"  sx={{ fontSize: 18 }}/>
                //   </InputAdornment>
                // ),
                endAdornment: (location.pathname.includes("/document")
                  ? searchTerm
                  : inputValue) && (
                    <InputAdornment position="end">
                      {loading && !location.pathname.includes("/document") ? (
                        <CircularProgress size={20} />
                      ) : (
                        <IconButton size="small" onClick={handleClearSearch}>
                          <CancelIcon fontSize="snall"/>
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
              }}
              sx={{
                width:
                  location.pathname.includes("/document")
                    ? isSmall
                      ? "100%"
                      : "220px"
                    : isSmall
                      ? "100%"
                      : "700px",
                bgcolor: "#fff",
                borderRadius: "50px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  fontFamily: fontFamilyall,
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: PageBackgroundColor },
                  "&.Mui-focused fieldset": { borderColor: PageBackgroundColor },
                },
                "& input": {
                  padding: isSmall ? "4px 12px" : "6px 20px",
                },
              }}
            />
          }

        </Box>
      )}
    </>
  );
};

export default NavbarWithSearch;
